import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import {
  apiError,
  type ApplyTemplateResult,
  type EmployeeRef,
  type ShiftTemplate,
} from './schedulingLib';

interface Props {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  dates: string[];
  templates: ShiftTemplate[];
  employees: EmployeeRef[];
  onApplied: () => void;
}

/**
 * Stamps one template across a set of dates in one act — the way a week
 * actually gets built (`applyTemplate` on the backend). It is NOT
 * transactional across dates: each date is its own `createShift` call, so a
 * conflict partway through leaves everything before it saved. Accepting
 * advisory conflicts up front is the one thing that keeps that from being a
 * routine occurrence; a BLOCKING conflict on one date still stops the run
 * there regardless.
 */
export default function ApplyTemplateDialog({
  open,
  onClose,
  scheduleId,
  dates,
  templates,
  employees,
  onApplied,
}: Props) {
  const { t } = useTranslation();
  const [templateId, setTemplateId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [acceptWarnings, setAcceptWarnings] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateId(templates[0]?.id ?? '');
      setEmployeeId('');
      setSelected(new Set(dates));
      setAcceptWarnings(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (date: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const submit = async () => {
    if (!templateId || selected.size === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post<ApplyTemplateResult>(
        `/scheduling/schedules/${scheduleId}/apply-template`,
        {
          templateId,
          dates: [...selected].sort(),
          employeeId: employeeId || undefined,
          acceptWarnings,
        },
      );
      if (data.created < selected.size) {
        toast.warning(
          t('scheduling.applyTemplate.partial', {
            created: data.created,
            requested: selected.size,
          }),
        );
      } else {
        toast.success(t('scheduling.applyTemplate.applied', { count: data.created }));
      }
      onApplied();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.applyTemplate.failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('scheduling.applyTemplate.title')}
      description={t('scheduling.applyTemplate.description')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={saving || !templateId || selected.size === 0} onClick={() => void submit()}>
            {t('scheduling.applyTemplate.apply')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('scheduling.shift.template')}>
          <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.startTime}–{tpl.endTime})
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('scheduling.shift.employee')} hint={t('scheduling.applyTemplate.employeeHint')}>
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">{t('scheduling.shift.openShift')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName ?? ''} ({emp.employeeCode})
              </option>
            ))}
          </Select>
        </Field>

        <div>
          <p className="mb-1.5 text-sm font-medium">{t('scheduling.applyTemplate.dates')}</p>
          <div className="grid grid-cols-4 gap-1.5">
            {dates.map((date) => (
              <label
                key={date}
                className="flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs has-[:checked]:border-accent-solid has-[:checked]:bg-accent-soft"
              >
                <input
                  type="checkbox"
                  checked={selected.has(date)}
                  onChange={() => toggle(date)}
                  className="h-3.5 w-3.5"
                />
                {date.slice(5)}
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acceptWarnings}
            onChange={(e) => setAcceptWarnings(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span>{t('scheduling.applyTemplate.acceptWarnings')}</span>
        </label>
      </div>
    </Dialog>
  );
}
